/**
 * EmailScanningSettings
 *
 * Settings page for the Email tab.
 * - Connect Microsoft 365 or Google via OAuth
 * - View and manage connected email accounts
 * - Trigger manual scans
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Clock,
  Loader2,
  Mail,
  Shield,
} from 'lucide-react';
import { useEmailScanning } from '@/hooks/useEmailScanning';
import { ConnectionList } from './ConnectionList';
import { IngestionAuditDashboard } from './IngestionAuditDashboard';

// Microsoft and Google brand icons (inline SVG to avoid dependencies)
const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export const EmailScanningSettings: React.FC = () => {
  const {
    connections,
    loadingConnections,
    loading,
    connectEmail,
    disconnectEmail,
    triggerManualScan,
    reconnectEmail,
  } = useEmailScanning();

  const hasConnections = connections.length > 0;
  const hasMicrosoft = connections.some((c) => c.provider === 'microsoft' && c.status !== 'needs_reauth');
  const hasGoogle = connections.some((c) => c.provider === 'google' && c.status !== 'needs_reauth');

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Email Scanning</p>
              <p className="text-xs text-muted-foreground mt-1">
                Connect your email to automatically find deal-related messages.
                Scans run at 11:00 AM ET and 9:00 PM ET daily. You can also scan manually anytime.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connect Email Section */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Connect Your Email</CardTitle>
              <CardDescription>
                Sign in with your email provider to grant read-only access
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Microsoft 365 Button */}
            <Button
              variant="outline"
              className="h-auto py-4 px-6 flex items-center gap-3 justify-start"
              onClick={() => connectEmail('microsoft')}
              disabled={loading || hasMicrosoft}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <MicrosoftIcon />
              )}
              <div className="text-left">
                <p className="font-medium text-sm">
                  {hasMicrosoft ? 'Microsoft 365 Connected' : 'Connect Microsoft 365'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Outlook, Office 365, Exchange Online
                </p>
              </div>
            </Button>

            {/* Google Button */}
            <Button
              variant="outline"
              className="h-auto py-4 px-6 flex items-center gap-3 justify-start"
              onClick={() => connectEmail('google')}
              disabled={loading || hasGoogle}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              <div className="text-left">
                <p className="font-medium text-sm">
                  {hasGoogle ? 'Google Connected' : 'Connect Google'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Gmail, Google Workspace
                </p>
              </div>
            </Button>
          </div>

          {/* Security Note */}
          <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <p>
              We request <strong>read-only</strong> access to your inbox. We never send, delete, or modify
              your emails. Your login is handled securely by Microsoft or Google — we never see your password.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Connection List */}
      <ConnectionList
        connections={connections}
        loading={loading}
        loadingConnections={loadingConnections}
        onDisconnect={disconnectEmail}
        onTriggerScan={triggerManualScan}
        onReconnect={reconnectEmail}
      />

      <IngestionAuditDashboard />

      {/* Scan Schedule Info */}
      {hasConnections && (
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Scan Schedule</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Automated scans run twice daily at <strong>11:00 AM ET</strong> and <strong>9:00 PM ET</strong>.
                  You can also trigger manual scans anytime using the refresh button on each connection.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
